import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Shield, Clock, CheckCircle, ArrowRight, Loader2 } from 'lucide-react'
import { Button, Card, CardContent, Input, Select, type SelectOption } from '@/components/ui'
import { getVehicleById } from '@/data/vehicles'
import { formatPrice } from '@/utils/format'

const employmentOptions: SelectOption[] = [
  { value: '', label: 'Select Employment Status' },
  { value: 'employed', label: 'Employed' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'student', label: 'Student' },
  { value: 'other', label: 'Other' },
]

const termOptions: SelectOption[] = [
  { value: '36', label: '36 months' },
  { value: '48', label: '48 months' },
  { value: '60', label: '60 months' },
  { value: '72', label: '72 months' },
  { value: '84', label: '84 months' },
]

type Step = 'personal' | 'employment' | 'financing' | 'review' | 'submitted'

export function ApplyPage() {
  const [searchParams] = useSearchParams()
  const vehicleId = searchParams.get('vehicleId')
  const vehicle = vehicleId ? getVehicleById(vehicleId) : null

  const [step, setStep] = useState<Step>('personal')
  const [submitting, setSubmitting] = useState(false)

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    employmentStatus: '',
    employer: '',
    jobTitle: '',
    monthlyIncome: '',
    yearsAtJob: '',
    downPayment: '5000',
    termMonths: '60',
  })

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    // Simulate API submission
    await new Promise((resolve) => setTimeout(resolve, 2000))
    setSubmitting(false)
    setStep('submitted')
  }

  const vehicleTitle = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`
    : null

  if (step === 'submitted') {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-lg mx-auto text-center">
            <CardContent className="py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="mt-6 text-2xl font-bold text-gray-900">
                Application Submitted!
              </h1>
              <p className="mt-2 text-gray-600">
                Thank you for your application. Our team will review it and
                contact you within 24 hours.
              </p>
              {vehicle && (
                <p className="mt-4 text-sm text-gray-500">
                  Vehicle: {vehicleTitle}
                </p>
              )}
              <div className="mt-8 space-y-3">
                <Link to="/account">
                  <Button className="w-full">View My Account</Button>
                </Link>
                <Link to="/vehicles">
                  <Button variant="outline" className="w-full">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Apply for Financing
          </h1>
          <p className="mt-3 text-lg text-gray-600">
            Complete your application in just a few minutes
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Benefits Banner */}
          <div className="mb-8 rounded-lg bg-primary-50 p-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary-600" />
                <span className="text-gray-700">Secure & Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary-600" />
                <span className="text-gray-700">Quick Decision</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span className="text-gray-700">No Credit Impact*</span>
              </div>
            </div>
          </div>

          {/* Vehicle Info (if selected) */}
          {vehicle && (
            <Card className="mb-6">
              <CardContent className="flex items-center gap-4">
                <div className="h-16 w-24 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                  {vehicle.photos[0] && (
                    <img
                      src={vehicle.photos[0]}
                      alt={vehicleTitle || ''}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Applying for:</p>
                  <p className="font-semibold text-gray-900">{vehicleTitle}</p>
                  <p className="text-primary-600 font-bold">
                    {formatPrice(vehicle.priceCents)}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm">
              {(['personal', 'employment', 'financing', 'review'] as const).map(
                (s, index) => (
                  <div
                    key={s}
                    className={`flex items-center ${index < 3 ? 'flex-1' : ''}`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        step === s
                          ? 'bg-primary-600 text-white'
                          : ['personal', 'employment', 'financing', 'review'].indexOf(
                              s
                            ) <
                            ['personal', 'employment', 'financing', 'review'].indexOf(
                              step
                            )
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                    {index < 3 && (
                      <div className="flex-1 h-1 mx-2 bg-gray-200">
                        <div
                          className={`h-full transition-all ${
                            ['personal', 'employment', 'financing', 'review'].indexOf(
                              s
                            ) <
                            ['personal', 'employment', 'financing', 'review'].indexOf(
                              step
                            )
                              ? 'bg-green-500 w-full'
                              : 'w-0'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>Personal</span>
              <span>Employment</span>
              <span>Financing</span>
              <span>Review</span>
            </div>
          </div>

          {/* Form Steps */}
          <Card>
            <CardContent className="p-6 sm:p-8">
              {step === 'personal' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Personal Information
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="First Name"
                      value={formData.firstName}
                      onChange={(e) => updateField('firstName', e.target.value)}
                      required
                    />
                    <Input
                      label="Last Name"
                      value={formData.lastName}
                      onChange={(e) => updateField('lastName', e.target.value)}
                      required
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      required
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      required
                    />
                  </div>
                  <Input
                    label="Street Address"
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    required
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Input
                        label="City"
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        required
                      />
                    </div>
                    <Input
                      label="State"
                      value={formData.state}
                      onChange={(e) => updateField('state', e.target.value)}
                      required
                    />
                    <Input
                      label="ZIP Code"
                      value={formData.zipCode}
                      onChange={(e) => updateField('zipCode', e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => setStep('employment')}>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 'employment' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Employment Information
                  </h2>
                  <Select
                    label="Employment Status"
                    options={employmentOptions}
                    value={formData.employmentStatus}
                    onChange={(e) => updateField('employmentStatus', e.target.value)}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Employer Name"
                      value={formData.employer}
                      onChange={(e) => updateField('employer', e.target.value)}
                    />
                    <Input
                      label="Job Title"
                      value={formData.jobTitle}
                      onChange={(e) => updateField('jobTitle', e.target.value)}
                    />
                    <Input
                      label="Monthly Income ($)"
                      type="number"
                      value={formData.monthlyIncome}
                      onChange={(e) => updateField('monthlyIncome', e.target.value)}
                    />
                    <Input
                      label="Years at Current Job"
                      type="number"
                      value={formData.yearsAtJob}
                      onChange={(e) => updateField('yearsAtJob', e.target.value)}
                    />
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep('personal')}>
                      Back
                    </Button>
                    <Button onClick={() => setStep('financing')}>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 'financing' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Financing Preferences
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Down Payment ($)"
                      type="number"
                      value={formData.downPayment}
                      onChange={(e) => updateField('downPayment', e.target.value)}
                    />
                    <Select
                      label="Loan Term"
                      options={termOptions}
                      value={formData.termMonths}
                      onChange={(e) => updateField('termMonths', e.target.value)}
                    />
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep('employment')}>
                      Back
                    </Button>
                    <Button onClick={() => setStep('review')}>
                      Review Application
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 'review' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Review Your Application
                  </h2>
                  <div className="space-y-4 text-sm">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">
                        Personal Information
                      </h3>
                      <p>
                        {formData.firstName} {formData.lastName}
                      </p>
                      <p>{formData.email}</p>
                      <p>{formData.phone}</p>
                      <p>
                        {formData.address}, {formData.city}, {formData.state}{' '}
                        {formData.zipCode}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">
                        Employment
                      </h3>
                      <p className="capitalize">{formData.employmentStatus}</p>
                      {formData.employer && <p>{formData.employer}</p>}
                      {formData.monthlyIncome && (
                        <p>Monthly Income: ${formData.monthlyIncome}</p>
                      )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">
                        Financing
                      </h3>
                      <p>Down Payment: ${formData.downPayment}</p>
                      <p>Loan Term: {formData.termMonths} months</p>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep('financing')}>
                      Back
                    </Button>
                    <Button onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Application'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <p className="mt-8 text-center text-xs text-gray-500">
            * Pre-qualification uses a soft credit inquiry that does not affect
            your credit score. A hard inquiry will be performed only if you
            choose to proceed with a formal credit application.
          </p>
        </div>
      </div>
    </div>
  )
}
